import { ApiError, downloadBlob, request, upload } from './client'
import type { Meta, NodeRoom, Settings, UsageRecord, UserImportResult } from './types'
import type {
  ActivityKind,
  ActivityList,
  ApiNode,
  Catalog,
  Cluster,
  Flavor,
  FlavorCreate,
  FlavorPatch,
  Image,
  InstallIso,
  Lease,
  LeaseApp,
  LeaseAppUpdate,
  LeaseApproval,
  LeaseChange,
  LeaseChangeApproval,
  LeaseChangeRequest,
  LeaseRoom,
  LeaseSpecSet,
  LeaseConsole,
  LeaseCreate,
  LeaseList,
  LeasePowerAction,
  LeaseSort,
  LeaseState,
  LeaseTags,
  MaintenanceResume,
  NodeCapacity,
  NodeBootLog,
  NodeCrashRecord,
  InstallerLine,
  InstallerRun,
  LiveVmState,
  NodeClusterDecisionRequest,
  NodeClusterMembership,
  NodeDisks,
  NodeGpuTest,
  NodeUsageSeries,
  NodeValidation,
  QuotaTier,
  QuotaTierCreate,
  QuotaTierPatch,
  ReviewItem,
  Session,
  SshKey,
  SshKeyList,
  StorageTier,
  Team,
  TeamInvitation,
  TeamMember,
  TeamRole,
  UsageTimeframe,
  Role,
  User,
  UserList,
  UserQuota,
  Vm,
  VmMetrics,
  VmUsageSeries,
} from './types'

const V1 = '/api/v1'

// 목록 봉투가 엔드포인트마다 다르다: clusters·leases·vms·users·teams 는 {items:[…]} 인데
// nodes 는 맨 배열이다. 그 차이를 여기서 흡수해 화면은 항상 배열만 본다.
type Items<T> = { items: T[] }

// 이 화면의 쓰기는 VM 신청, 공용 설치 ISO 생성, 운영 복귀 예약과 서버가 허용한 클러스터
// 소속 결정이다. 생성된 접속 키의 단회 다운로드까지가 여기 있고, 수동 검증·승격은 없다.

export const auth = {
  login: (username: string, password: string) =>
    request<Session>(`${V1}/auth/login`, {
      method: 'POST',
      body: { username, password },
      noAuth: true,
    }),
  logout: () => request<void>(`${V1}/auth/logout`, { method: 'POST' }),
  me: () => request<User>(`${V1}/auth/me`),
  /** 내 정보(F-37): 이름만 내가 바꾼다. */
  updateMe: (name: string) => request<User>(`${V1}/auth/me`, { method: 'PATCH', body: { name } }),
  meta: () => request<Meta>(`${V1}/meta`, { noAuth: true }),
  /** 계정 신청(F-14). 비밀번호는 보내지 않는다 — 메일의 인증 링크에서 정한다(F-33). */
  register: (username: string, name: string, email: string, password: string) =>
    request<void>(`${V1}/auth/register`, { method: 'POST', body: { username, name, email, password }, noAuth: true }),
  changePassword: (current_password: string, new_password: string) =>
    request<void>(`${V1}/auth/password`, { method: 'POST', body: { current_password, new_password } }),
  /** 메일 링크의 토큰들(F-33). 전부 로그인 전이라 noAuth. */
  /** 비밀번호는 신청에 있다(F-120). password 는 그 전에 만든 신청이 password_required 로 답할 때만 싣는다. */
  verify: (token: string, password?: string) =>
    request<void>(`${V1}/auth/verify`, { method: 'POST', body: password ? { token, password } : { token }, noAuth: true }),
  requestReset: (email: string) => request<void>(`${V1}/auth/password/reset`, { method: 'POST', body: { email }, noAuth: true }),
  confirmReset: (token: string, new_password: string) =>
    request<void>(`${V1}/auth/password/reset/confirm`, { method: 'POST', body: { token, new_password }, noAuth: true }),
}

/** 가입 신청의 결정(F-14, 관리자). 승인은 인증 때 정한 비밀번호로 계정을 만든다. */
export const signups = {
  approve: (id: string) => request<User>(`${V1}/signups/${id}/approve`, { method: 'POST' }),
  reject: (id: string) => request<void>(`${V1}/signups/${id}/reject`, { method: 'POST' }),
}

export const clusters = {
  list: async () => (await request<Items<Cluster>>(`${V1}/clusters`)).items,
  remove: (id: string) => request<Cluster>(`${V1}/clusters/${id}`, { method: 'DELETE' }),
  rename: (id: string, name: string) => request<Cluster>(`${V1}/clusters/${id}`, { method: 'PATCH', body: { name } }),
}

export const installIso = {
  current: async (): Promise<InstallIso | null> => {
    try {
      return await request<InstallIso>(`${V1}/install/iso`)
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  },
  create: () => request<InstallIso>(`${V1}/install/iso`, { method: 'POST' }),
  download: async () => {
    const iso = await request<InstallIso>(`${V1}/install/iso`, { query: { download: 'true' } })
    const path = iso.download_url
    if (!path) throw new Error('설치 ISO 를 받을 수 없습니다.')
    if (!path.startsWith(`${V1}/install/iso/download?d=`)) {
      throw new Error('설치 ISO 내려받기 경로가 아닙니다.')
    }
    window.location.assign(path)
  },
}

export const nodes = {
  list: () => request<ApiNode[]>(`${V1}/nodes`),
  /** 노드 하나. 목록에 없는 값(관리 이미지 사본, #331)이 여기에만 있다. */
  get: (id: string) => request<ApiNode>(`${V1}/nodes/${id}`),
  capacity: (id: string) => request<NodeCapacity>(`${V1}/nodes/${id}/capacity`),
  /** 노드의 디스크와 데이터 풀(A6-12). 우리 행이라 닿지 않는 노드도 답한다. */
  disks: (id: string) => request<NodeDisks>(`${V1}/nodes/${id}/disks`),
  /** 지난 부팅들이 어떻게 끝났나(A6-16). 고급에서만 읽는다 — 글이 크고 묻는 일이 드물다. */
  bootLogs: (id: string) => request<NodeBootLog[]>(`${V1}/nodes/${id}/boot-logs`),
  /** 크래시 때 펌웨어가 남긴 커널 덤프(#412). 재설치로 지워지지 않는다. 고급에서만 읽는다. */
  crashRecords: (id: string) => request<NodeCrashRecord[]>(`${V1}/nodes/${id}/crash-records`),
  installerRuns: (id: string) => request<{ items: InstallerRun[] }>(`${V1}/nodes/${id}/installer-runs`).then((r) => r.items),
  installerRun: (id: string, run: string) =>
    request<{ items: InstallerLine[] }>(`${V1}/nodes/${id}/installer-runs/${run}`).then((r) => r.items),
  /** 임대 디스크 단계의 순서만 바꾼다(B-26). 예약은 건드리지 않고, 답은 바뀐 원장이다. */
  setStorageOrder: (id: string, lease_storage_order: StorageTier[]) =>
    request<NodeCapacity>(`${V1}/nodes/${id}/capacity`, { method: 'PATCH', body: { lease_storage_order } }),
  /** PVE RRD 를 요청 시점에 읽은 시계열. 닿지 않던 구간은 null 로 온다. */
  usage: (id: string, timeframe: UsageTimeframe) =>
    request<NodeUsageSeries>(`${V1}/nodes/${id}/usage`, { query: { timeframe } }),
  validations: (id: string) => request<NodeValidation[]>(`${V1}/nodes/${id}/validations`),
  /** 다음 부팅에 연결하고 시험할 GPU 를 정한다(#427). 재부팅은 사람이 한다. */
  gpuTest: (id: string, body: NodeGpuTest) => request<NodeGpuTest>(`${V1}/nodes/${id}/gpu-test`, { method: 'PUT', body }),
  resumeAfterMaintenance: (id: string) =>
    request<MaintenanceResume>(`${V1}/nodes/${id}/maintenance/resume`, { method: 'POST' }),
  clusterMembership: (id: string) =>
    request<NodeClusterMembership>(`${V1}/nodes/${id}/cluster-membership`),
  decideCluster: (id: string, body: NodeClusterDecisionRequest) =>
    request<NodeClusterMembership>(`${V1}/nodes/${id}/cluster-membership/decision`, { method: 'POST', body }),
  leaveCluster: (id: string) =>
    request<NodeClusterMembership>(`${V1}/nodes/${id}/cluster/leave`, { method: 'POST' }),
  /** 화면 이름만 바꾼다. PVE 호스트명은 설치 때 정해진 그대로다. */
  rename: (id: string, display_name: string) =>
    request<ApiNode>(`${V1}/nodes/${id}`, { method: 'PATCH', body: { display_name } }),
  /** 오프라인 노드를 제거한다(#269): 멤버면 남은 멤버를 통해 클러스터에서 빼고, 행은 숨긴다. 답은 204 다. */
  remove: (id: string) => request<void>(`${V1}/nodes/${id}`, { method: 'DELETE' }),
  /** 시스템이 설치 때 정한 root 비밀번호(A0-5). 볼 때마다 노드 이벤트가 남는다. 값은 state 에만 두고 화면을 떠나면 사라진다. */
  rootPassword: async (id: string) => (await request<{ root_password: string }>(`${V1}/nodes/${id}/root-password`)).root_password,
  /** 노드를 재부팅한다(#480). 게스트가 있으면 확인 전에는 409 node_has_guests 와 그 목록이 온다. */
  reboot: (id: string, confirm_guests: boolean) =>
    request<{ node_id: string; restart: number }>(`${V1}/nodes/${id}/reboot`, { method: 'POST', body: { confirm_guests } }),
  /** 노드의 root 셸을 준비한다(#480). 답은 단회 WebSocket 경로다 — 여는 사람의 것이고 1분 산다. */
  shell: (id: string) => request<{ url: string; expires_at: string }>(`${V1}/nodes/${id}/shell`, { method: 'POST' }),
  /** 편입 노드에 관리 도구를 올린다(#101). 202 뒤의 진행은 노드의 국면이 말한다. 비밀번호는 본문으로만 간다. */
  onboard: (id: string, root_password: string) =>
    request<unknown>(`${V1}/nodes/${id}/onboard`, { method: 'POST', body: { root_password } }),
}

/**
 * `GET /leases` 의 거르개·찾기·정렬. 모두 보는 사람이 볼 수 있는 임대 안에서 좁히기만 한다.
 * 반복하는 인자(`state`·`vm_state`·`q`)는 그 수만큼 싣는다. 빈 찾는 말은 싣지 않는다.
 */
export type LeaseQuery = {
  state?: LeaseState[]
  vm_state?: LiveVmState[]
  unreachable?: boolean
  pending_change?: boolean
  node?: string
  /** 그 사람의 임대만 — 관리자는 누구든, 그 밖은 자기 자신만이다. */
  user?: string
  /** 찾는 말 — 화면의 검색 칸과 상단 바의 것, 둘까지이고 모두 맞아야 한다. */
  q?: string[]
  sort?: LeaseSort
  dir?: 'asc' | 'desc'
}

/** 찾는 말 하나의 상한(스펙의 `maxLength`). 넘는 말은 앞부분으로 찾는다 — 400 으로 화면이 깨지지 않게. */
const TERM_MAX = 100

/** 찾는 말을 싣는다 — 빈 말은 빼고 둘까지(스펙의 `maxItems`), 하나는 상한까지. 임대와 계정 목록이 같다. */
function appendTerms(p: URLSearchParams, terms: string[] | undefined): void {
  for (const t of (terms ?? []).map((t) => t.trim()).filter(Boolean).slice(0, 2)) p.append('q', t.slice(0, TERM_MAX))
}

function leaseParams(q: LeaseQuery & { limit?: number; offset?: number }): string {
  const p = new URLSearchParams()
  for (const s of q.state ?? []) p.append('state', s)
  for (const s of q.vm_state ?? []) p.append('vm_state', s)
  if (q.unreachable !== undefined) p.set('unreachable', String(q.unreachable))
  if (q.pending_change !== undefined) p.set('pending_change', String(q.pending_change))
  if (q.node) p.set('node', q.node)
  if (q.user) p.set('user', q.user)
  appendTerms(p, q.q)
  if (q.sort) {
    p.set('sort', q.sort)
    if (q.dir) p.set('dir', q.dir)
  }
  if (q.limit) p.set('limit', String(q.limit))
  if (q.limit && q.offset) p.set('offset', String(q.offset))
  const query = p.toString()
  return `${V1}/leases${query ? `?${query}` : ''}`
}

export const leases = {
  /** 거르개에 맞는 임대 전부(쪽 없음). 관리자 세션은 볼 수 있는 모든 임대에서, 사용자는 자기 것과 팀의 것에서 좁힌다. */
  list: async (q: LeaseQuery = {}) => (await request<LeaseList>(leaseParams(q))).items,
  /** 한 쪽 — `limit` 개를 `offset` 부터. `total` 은 거르개·찾기에 맞는 수이고 `counts` 는 목록 머리의 수다. */
  page: (q: LeaseQuery & { limit: number; offset: number }) => request<LeaseList>(leaseParams(q)),
  get: (id: string) => request<Lease>(`${V1}/leases/${id}`),
  /** PVE 가 VM 마다 갖고 있는 시계열을 요청 시점에 읽는다(#257). 정지된 VM 도 이력이 있다. */
  usage: (id: string, timeframe: UsageTimeframe) =>
    request<VmUsageSeries>(`${V1}/leases/${id}/usage`, { query: { timeframe } }),
  /**
   * 202 — 카드를 받는 신청(고른 GPU, 고르지 않았으면 사양의 카드)은 승인 대기로, 아니면 바로 배치로 간다. 관리자 이상의 신청은 카드가 있어도 바로 배치된다(B-42).
   * 카드를 지목하는 길은 없다 — 모델과 장 수만 고른다(F-96).
   * 응답에 `ssh_private_key_download_url` 이 있으면 호출자가 즉시 `files.downloadOnce` 로 받고 링크를 버린다.
   */
  create: (body: LeaseCreate) => request<Lease>(`${V1}/leases`, { method: 'POST', body }),
  /** 202 — 종료·회수가 시작된다. 같은 요청이 겹쳐도 202 다. 비활성 임대는 409. */
  end: (id: string, reason?: string) =>
    request<Lease>(`${V1}/leases/${id}/end`, { method: 'POST', body: reason ? { reason } : {} }),
  /**
   * 200 — 전원 조작(B-22)이 끝난 뒤 돌아온다. PVE 의 작업을 기다리므로 종료·재부팅은 수십 초가 걸릴 수 있다.
   * 응답의 `vm.state` 가 PVE 가 그 뒤에 말한 상태다. 비활성 임대·맞지 않는 상태·진행 중이면 409, 클러스터 실패는 502.
   */
  /**
   * 202 — GPU 신청을 통과시킨다. 자리가 없어도 승인은 남고 자원을 기다린다. 승인 대기가 아니면 409.
   * 본문은 고친 카드뿐이고, 없으면 신청한 그대로 통과한다.
   */
  approve: (id: string, body?: LeaseApproval) =>
    request<Lease>(`${V1}/leases/${id}/approve`, { method: 'POST', ...(body ? { body } : {}) }),
  /** 200 — 사유는 필수다. 신청자가 그 사유를 읽는다. */
  reject: (id: string, reason: string) => request<Lease>(`${V1}/leases/${id}/reject`, { method: 'POST', body: { reason } }),
  purge: (id: string) => request<Lease>(`${V1}/leases/${id}/purge`, { method: 'POST' }),
  /** 202 — 변경 신청(B-27·B-35). 본문은 기계가 가질 숫자다. 모든 변경은 최고 관리자 승인을 기다리고, 열린 변경이 있으면 409. */
  requestChange: (id: string, body: LeaseChangeRequest) =>
    request<LeaseChange>(`${V1}/leases/${id}/changes`, { method: 'POST', body }),
  /** 202 — 최고 관리자가 사양을 바로 정한다(B-43). 승인된 변경으로 남고 다음 재부팅이 적용한다. 직위 한도는 묻지 않는다. */
  setSpec: (id: string, body: LeaseSpecSet) => request<LeaseChange>(`${V1}/leases/${id}/spec`, { method: 'POST', body }),
  /** VM 이 있는 노드에 남은 것 — 변경·수정·승인 창이 고를 수 있는 것은 이것이다. 소유자와 최고 관리자만 읽는다. */
  room: (id: string) => request<LeaseRoom>(`${V1}/leases/${id}/room`),
  changes: async (id: string) => (await request<Items<LeaseChange>>(`${V1}/leases/${id}/changes`)).items,
  /** 202 — 허락한 숫자와 함께 통과시킨다. 본문이 없으면 신청한 그대로다. 적용은 소유자의 다음 재부팅이다(B-29). */
  approveChange: (id: string, change: string, body?: LeaseChangeApproval) =>
    request<LeaseChange>(`${V1}/leases/${id}/changes/${change}/approve`, { method: 'POST', ...(body ? { body } : {}) }),
  rejectChange: (id: string, change: string, reason: string) =>
    request<LeaseChange>(`${V1}/leases/${id}/changes/${change}/reject`, { method: 'POST', body: { reason } }),
  /** 200 — 신청자가 기다리는 변경을 거둔다. */
  cancelChange: (id: string, change: string) =>
    request<LeaseChange>(`${V1}/leases/${id}/changes/${change}/cancel`, { method: 'POST' }),
  restore: (id: string) => request<Lease>(`${V1}/leases/${id}/restore`, { method: 'POST' }),
  power: (id: string, action: LeasePowerAction) =>
    request<Lease>(`${V1}/leases/${id}/power`, { method: 'POST', body: { action } }),
  /**
   * 200 — 기계의 키 쌍을 새로 만들고 VM 의 authorized_keys 를 있어야 할 집합으로 덮어쓴다(#226). 소유자·최고 관리자
   * (소유자가 사용자로 속한 팀의 관리자는 403 lease_not_yours).
   * 응답의 `ssh_private_key_download_url` 로 새 개인키를 다시 받는다. 비활성·정지·에이전트 무응답은 409, 쓰기 실패는 502.
   */
  rotateKey: (id: string) => request<Lease>(`${V1}/leases/${id}/ssh-key/rotate`, { method: 'POST' }),
  /**
   * 200 — 콘솔 하나를 연다(D-1). 답의 url 로 WebSocket 을 열고 vnc_ticket 을 게스트에게 낸다. 소유자·팀원·최고 관리자·
   * 소유자가 사용자로 속한 팀의 관리자; 팀 viewer 는 403. 정지·작업 중·비활성은 409, 콘솔 사용자가 아직 없으면 409(console_unavailable), 클러스터 거절은 502.
   */
  console: (id: string) => request<LeaseConsole>(`${V1}/leases/${id}/console`, { method: 'POST' }),
  attachDisk: (id: string, size_gb: number) =>
    request<Lease>(`${V1}/leases/${id}/disks`, { method: 'POST', body: { size_gb } }),
  resizeDisk: (id: string, slot: number, size_gb: number) =>
    request<Lease>(`${V1}/leases/${id}/disks/${slot}`, { method: 'PUT', body: { size_gb } }),
  removeDisk: (id: string, slot: number) =>
    request<Lease>(`${V1}/leases/${id}/disks/${slot}`, { method: 'DELETE' }),
  /** 기계가 공개하는 앱(D-5). 소유자·팀원·최고 관리자·소유자가 속한 팀의 관리자가 읽는다. */
  app: (id: string) => request<LeaseApp>(`${V1}/leases/${id}/app`),
  /** 두 포트를 한 번에 정한다(소유자·최고 관리자, 소유자가 사용자로 속한 팀의 관리자는 403). null 은 그 역할을 공개하지 않는 것. active 가 아니면 409, 22 는 400. */
  setApp: (id: string, body: LeaseAppUpdate) =>
    request<LeaseApp>(`${V1}/leases/${id}/app`, { method: 'PUT', body }),
  /** 200 — 태그를 보낸 것으로 바꾼다(F-95). 소유자·최고 관리자(소유자가 사용자로 속한 팀의 관리자는 403 lease_not_yours). */
  setTags: (id: string, tags: string[]) =>
    request<LeaseTags>(`${V1}/leases/${id}/tags`, { method: 'PUT', body: { tags } }),
}

/** 실행 중인 VM 들의 지금 읽기. 초 단위로 다시 묻는 값이라 변경 알림이 아니라 polling 이다. */
export const vmMetrics = {
  list: async () => (await request<Items<VmMetrics>>(`${V1}/vm-metrics`)).items,
  /** 고른 임대만 — 상세 화면의 하나, 목록의 한 쪽. lease_id 는 반복 쿼리라 client 의 query 옵션 대신 여기서 잇는다. */
  listFor: async (leaseIds: string[]) =>
    (await request<Items<VmMetrics>>(`${V1}/vm-metrics?${leaseIds.map((id) => `lease_id=${encodeURIComponent(id)}`).join('&')}`)).items,
}

// 내 공개키(B-10, #227). 등록·삭제는 게이트웨이와 도는 VM 에 곧 반영된다. 기계 키를 지우면 409(reason key_in_use).
// 목록은 한 번 붙이는 ssh config 를 함께 싣는다(#626).
export const sshKeys = {
  list: () => request<SshKeyList>(`${V1}/me/ssh-keys`),
  create: (body: { name: string; public_key: string }) =>
    request<SshKey>(`${V1}/me/ssh-keys`, { method: 'POST', body }),
  remove: (id: string) => request<void>(`${V1}/me/ssh-keys/${id}`, { method: 'DELETE' }),
}

export const files = {
  /**
   * 세션 bearer 로 한 번 받는 파일. 백엔드가 준 상대 경로(`/api/v1/…?d=…`)만 받는다 —
   * 다른 origin 으로 토큰이 새지 않게.
   */
  downloadOnce: (path: string) => {
    if (!path.startsWith('/api/v1/')) throw new Error('백엔드 경로가 아닌 링크는 받지 않는다')
    return downloadBlob(path)
  },
}

// 실적 보고서(B-15). 세션으로 받아 그 자리에서 저장하고 버린다 — 한 파일에
// 모든 사람의 사용량이 들어 있어 주소를 남길 것이 아니다.
export const reports = {
  /** 기록이 덮는 범위. 화면은 이 밖의 기간을 고르지 못하게 한다. */
  usageRecord: () => request<UsageRecord>(`${V1}/reports/usage`),
  usageXlsx: (from: string, to: string) =>
    files.downloadOnce(`${V1}/reports/usage.xlsx?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
}

export const vms = {
  /**
   * 관리자에게 보이는 VM. `team` 은 그 팀원의 것으로 좁히고(볼 수 없는 팀이면 빈 목록), `live` 는 묘비·오류를 뺀다 —
   * 둘을 함께 주면 팀 줄의 `vm_count` 가 센 집합이다.
   */
  list: async (q: { team?: string; live?: boolean } = {}) =>
    (await request<Items<Vm>>(`${V1}/vms`, { query: { team: q.team, live: q.live ? 'true' : undefined } })).items,
}

/**
 * `GET /users` 의 거르개·찾기. 모두 보는 사람이 받는 목록 안에서 좁히기만 한다. 순서는 역할, 그다음 아이디다.
 * 찾는 말(`q`)은 그 수만큼 싣는다 — 빈 말은 싣지 않는다.
 */
export type UserQuery = {
  /** 찾는 말 — 화면의 검색 칸과 상단 바의 것, 둘까지이고 모두 맞아야 한다. */
  q?: string[]
  /** 참이면 탈퇴한 계정만, 거짓이면 탈퇴하지 않은 계정만. 없으면 둘 다다. */
  withdrawn?: boolean
  /** 그 팀의 멤버만 — 보는 사람이 볼 수 없는 팀이면 빈 목록이다. */
  team?: string
  /** 그 직위의 사람만. */
  tier?: string
}

function userParams(q: UserQuery & { limit?: number; offset?: number }): string {
  const p = new URLSearchParams()
  appendTerms(p, q.q)
  if (q.withdrawn !== undefined) p.set('withdrawn', String(q.withdrawn))
  if (q.team) p.set('team', q.team)
  if (q.tier) p.set('tier', q.tier)
  if (q.limit) p.set('limit', String(q.limit))
  if (q.limit && q.offset) p.set('offset', String(q.offset))
  const query = p.toString()
  return `${V1}/users${query ? `?${query}` : ''}`
}

export const users = {
  /** 거르개에 맞는 계정 전부(쪽 없음). */
  list: async (q: UserQuery = {}) => (await request<UserList>(userParams(q))).items,
  /** 한 쪽 — `limit` 개를 `offset` 부터. `total` 은 거르개·찾기에 맞는 수이고 `counts` 는 절의 머리 수다. */
  page: (q: UserQuery & { limit: number; offset: number }) => request<UserList>(userParams(q)),
  /** 한 사람. 그 사람에게 닿을 수 없는 사람에게는 없는 사람과 같은 404 다. */
  get: (id: string) => request<User>(`${V1}/users/${id}`),
  /**
   * 그 사람이 지금 지는 한도와 그 숫자가 온 직위(F-72). 읽기뿐이다 — 사람마다 적는 길은 없고,
   * 한도를 바꾸는 것은 직위를 바꾸는 것이다(`quotaTiers.put`).
   */
  quota: (id: string) => request<UserQuota>(`${V1}/users/${id}/quota`),
  /** 최고 관리자의 승격·강등(F-38). 자기 자신은 409 `self`. */
  setRole: (id: string, role: Role) => request<User>(`${V1}/users/${id}/role`, { method: 'PUT', body: { role } }),
  /** 관리자의 탈퇴 처리(F-36). 자기 자신은 409 `self`, 이미 탈퇴는 409 `already_withdrawn`. */
  withdraw: (id: string) => request<User>(`${V1}/users/${id}/withdraw`, { method: 'POST' }),
  /**
   * 엑셀 일괄 등록(F-77). `dryRun` 이면 아무것도 쓰지 않고 줄마다 판정만 돌려준다.
   * 메일을 보낼 수 없는 배포는 409 `mail_unavailable`, 읽을 수 없는 파일은 400.
   */
  importXlsx: (file: File, dryRun: boolean) => {
    const form = new FormData()
    form.append('file', file)
    return upload<UserImportResult>(`${V1}/users/import${dryRun ? '?dry_run=true' : ''}`, form)
  },
  /** 일괄 등록 양식 — 머리 줄만 있는 빈 통합 문서. */
  importTemplate: () => files.downloadOnce(`${V1}/users/import/template.xlsx`),
}

// 직위(F-72) — 쿼터를 가진 이름. **최고 관리자만 읽고 쓴다**: 숫자가 전역이라 한정 관리자가
// 고치면 자기 팀 밖 사람의 한도가 함께 움직인다.
export const quotaTiers = {
  list: async () => (await request<Items<QuotaTier>>(`${V1}/quota-tiers`)).items,
  /** 204 — 모든 직위의 순서를 한 번에(F-122). 목록이 낡았으면 409 `quota_tier_order_stale` 이고 아무것도 움직이지 않는다. */
  setOrder: (ids: string[]) => request<void>(`${V1}/quota-tiers/order`, { method: 'PUT', body: { ids } }),
  /** 비운 축은 본문에 싣지 않는다 — 생략이 곧 자유다. 적는 것은 이름 하나이고 겹쳐도 된다. */
  create: (body: QuotaTierCreate) => request<QuotaTier>(`${V1}/quota-tiers`, { method: 'POST', body }),
  /** `limits` 를 실으면 다섯 축 전체 교체(빠진 축은 자유), 싣지 않으면 다섯을 건드리지 않는다. */
  patch: (id: string, body: QuotaTierPatch) => request<QuotaTier>(`${V1}/quota-tiers/${id}`, { method: 'PATCH', body }),
  /** 새 계정이 들어가는 자리. 하나뿐이라 올리는 것이 먼저 있던 것을 내린다. */
  setDefault: (id: string) => request<QuotaTier>(`${V1}/quota-tiers/${id}/default`, { method: 'POST' }),
  /**
   * 204 — 직위를 지운다. 기본 직위는 409 `is_default`, 사람이 든 직위는 409 `in_use` 이고
   * 그 거절은 인원(`people`)을 싣는다 — 기본 직위는 새 계정이 들어가는 자리고, 사람이 든 직위는 그 사람들의 한도다.
   */
  remove: (id: string) => request<void>(`${V1}/quota-tiers/${id}`, { method: 'DELETE' }),
  /**
   * 사람 하나를 직위에 넣는다. 답은 그 사람이 지금 지는 한도다. 없는 사람은 404 `user_not_found`,
   * 없는 직위는 404 `quota_tier_not_found`, 탈퇴한 사람은 409 `already_withdrawn`.
   */
  put: (user: string, quota_tier_id: string) =>
    request<UserQuota>(`${V1}/users/${user}/quota-tier`, { method: 'PUT', body: { quota_tier_id } }),
}

// 플랫폼의 스위치(F-76). 최고 관리자만 — 회원가입을 받을지가 지금의 전부다.
export const settings = {
  get: () => request<Settings>(`${V1}/settings`),
  put: (body: Settings) => request<Settings>(`${V1}/settings`, { method: 'PUT', body }),
}

export const teams = {
  list: async () => (await request<Items<Team>>(`${V1}/teams`)).items,
  /** 팀을 연다(F-46). 소유자는 만든 사람이다 — 최고 관리자만 다른 사람을 지정한다. 식별자를 비우면 백엔드가 정한다. */
  create: (body: { name?: string; display_name: string; tags?: string[] }) =>
    request<Team>(`${V1}/teams`, { method: 'POST', body }),
  /** 이름·사용 여부·태그를 고친다. 태그는 보낸 것이 그 팀의 전부가 된다. */
  patch: (id: string, body: { display_name?: string; enabled?: boolean; tags?: string[] }) =>
    request<Team>(`${V1}/teams/${id}`, { method: 'PATCH', body }),
  /**
   * 204 — 팀을 지운다. 고칠 수 있는 사람(최고 관리자·그 팀의 관리자)의 것이다. 팀으로 신청한 VM 이 남았으면
   * 409 `team_in_use` 이고 그 거절은 남은 수(`leases`)를 싣는다.
   */
  remove: (id: string) => request<void>(`${V1}/teams/${id}`, { method: 'DELETE' }),
  /** 팀에서의 자리를 정한다 — `owner` 가 그 팀을 관리한다. */
  setMember: (id: string, user: string, role: TeamRole) =>
    request<TeamMember>(`${V1}/teams/${id}/members/${user}`, { method: 'PUT', body: { role } }),
  /** 204 — 팀에서 뺀다. 마지막 소유자는 뺄 수 없다(409). */
  removeMember: (id: string, user: string) =>
    request<void>(`${V1}/teams/${id}/members/${user}`, { method: 'DELETE' }),
  /** 내가 든 팀 — 사용자 화면의 팀 목록이다. 관리자의 `list` 는 모든 팀이다. */
  mine: async () => (await request<Items<Team>>(`${V1}/me/teams`)).items,
  /**
   * 초대(F-92). 일반 사용자인 팀 관리자는 새 사람을 `setMember` 로 넣지 못하고(403 `invite_required`)
   * 아이디로 초대한다 — 받은 사람이 수락하면 멤버로 든다. 초대하고 거두는 것은 그 팀의 관리자다.
   */
  invite: (id: string, username: string) =>
    request<TeamInvitation>(`${V1}/teams/${id}/invitations`, { method: 'POST', body: { username } }),
  /** 그 팀이 답을 기다리는 초대. */
  invitations: async (id: string) => (await request<Items<TeamInvitation>>(`${V1}/teams/${id}/invitations`)).items,
  /** 204 — 답하지 않은 초대를 거둔다. */
  cancelInvitation: (id: string, invitation: string) =>
    request<void>(`${V1}/teams/${id}/invitations/${invitation}`, { method: 'DELETE' }),
  /** 내게 온, 답을 기다리는 초대. */
  myInvitations: async () => (await request<Items<TeamInvitation>>(`${V1}/me/team-invitations`)).items,
  /** 204 — 수락하면 그 팀의 멤버가 된다. 초대받은 본인만. */
  accept: (invitation: string) => request<void>(`${V1}/team-invitations/${invitation}/accept`, { method: 'POST' }),
  /** 204 — 거절. 팀은 다시 초대할 수 있다. */
  decline: (invitation: string) => request<void>(`${V1}/team-invitations/${invitation}/decline`, { method: 'POST' }),
}

export const catalog = {
  /** 신청자가 고를 수 있는 전부를 한 번에 — 사양·이미지·디스크 단계·GPU 모델·내 쿼터. VM 생성 페이지와 변경 대화 상자가 읽는다. */
  get: () => request<Catalog>(`${V1}/catalog`),
  /** 노드마다 전체·할당·남음과 모델별 카드(B-37). 보기만 한다 — 배치는 서버가 정한다. */
  nodes: () => request<NodeRoom[]>(`${V1}/catalog/nodes`),
  flavors: async () => (await request<Items<Flavor>>(`${V1}/flavors`)).items,
  images: async () => (await request<Items<Image>>(`${V1}/images`)).items,
  /** 새 사양. 비운 자원은 본문에 싣지 않는다 — 생략이 곧 자유다(F-62). 이름이 겹치면 409. */
  createFlavor: (body: FlavorCreate) => request<Flavor>(`${V1}/flavors`, { method: 'POST', body }),
  /** 만든 뒤에 고치는 것 — 표시 이름·순서·신청 가능뿐이다. 자원 숫자는 움직이지 않는다. */
  patchFlavor: (id: string, body: FlavorPatch) => request<Flavor>(`${V1}/flavors/${id}`, { method: 'PATCH', body }),
  /** 204. 모든 사양을 한 번씩, 앞에서부터. 빠지거나 겹치면 409 `flavor_order_stale` — 목록을 다시 읽는다. */
  setFlavorOrder: (ids: string[]) => request<void>(`${V1}/flavors/order`, { method: 'PUT', body: { ids } }),
  /** 204. 그 사양으로 만든 VM 과 무관하게 지워진다(F-107) — 임대는 신청 때 옮겨 적은 숫자를 갖는다. */
  deleteFlavor: (id: string) => request<void>(`${V1}/flavors/${id}`, { method: 'DELETE' }),
  /** 204. 신청이나 VM 이 가리키는 이미지는 409 `image_in_use`(반려된 신청도), 노드에 사본을 두는 관리 이미지는 409 `image_managed`. */
  deleteImage: (id: string) => request<void>(`${V1}/images/${id}`, { method: 'DELETE' }),
}

// 검토 대기(F-32). 항목은 서버가 조합하고, 항목의 조작은 각 주체의 엔드포인트다 — 여기는 목록과 무시뿐이다.
// 작업 내역(F-30). 쪽 번호를 그리는 화면이라 offset 페이지이고, `total` 이 쪽 수를 준다.
export const events = {
  list: (q: { kind?: ActivityKind; limit?: number; offset?: number } = {}) => {
    const p = new URLSearchParams()
    if (q.kind) p.set('kind', q.kind)
    if (q.limit) p.set('limit', String(q.limit))
    if (q.offset) p.set('offset', String(q.offset))
    const query = p.toString()
    return request<ActivityList>(`${V1}/events${query ? `?${query}` : ''}`)
  },
}

export const review = {
  list: async () => (await request<Items<ReviewItem>>(`${V1}/review`)).items,
  /** 204 — 그 국면(episode)만 사라진다. 결정이 필요한 항목은 409. */
  dismiss: (item: Pick<ReviewItem, 'kind' | 'subject_id' | 'episode'>) =>
    request<void>(`${V1}/review/dismiss`, {
      method: 'POST',
      body: { kind: item.kind, subject_id: item.subject_id, episode: item.episode },
    }),
}
